import { FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { snakeCase } from 'change-case';
import { each, keyBy, set, sumBy, valuesIn } from 'lodash-es';
import { FormProvider, useForm } from 'react-hook-form';
import { CgMathPercent } from 'react-icons/cg';
import { useSearchParams } from 'react-router';
import { Box, Flex, Heading, List, Stack, Text } from '@chakra-ui/react';

import { snakeCaseToSentenceCase } from '~/core/application/workflow/utils/transformFuncs';
import {
  CurrencyInput,
  DatePattern,
  DatePicker,
  FormField,
  Input,
  NumberInput,
  Select,
  SelectProps,
  Textarea,
  Wysiwyg,
} from '~/core/shared/components';
import { Permissions } from '~/core/shared/generated/Permissions';
import { useCan } from '~/core/shared/hooks/useCan';
import {
  ApplicationForm,
  ApplicationFormBlock,
  ApplicationFormInputField,
  ApplicationFormSelectField,
  FormContext,
  FormContextEntry,
  Nullable,
  Option,
  OptionGroup,
} from '~/core/shared/types';
import { Button } from '../../Button.component';

type ApplicationUpdateFormProps = {
  form: ApplicationForm;
  formContexts: Record<string, FormContext>;
  isSaving: boolean;
  validationErrors: Record<string, any>;
  onSubmit: (formValues: any) => Promise<void>;
  hideLeftPanel?: boolean;
  showHiddenSave?: boolean;
  onlyDirtyFields?: boolean;
};

type FormSectionTypes = {
  parent: {
    id: string;
    title: string;
    blocks: ApplicationFormBlock[];
  };
  children: ApplicationFormBlock[];
};

type SideNavTypes = {
  hideLeftPanel: boolean | undefined;
  initialSectionRefs: Record<string, Nullable<HTMLHeadingElement>>;
  formContexts: Record<string, FormContext>;
  formSections: FormSectionTypes[];
  scrollTo: (ref: Nullable<HTMLHeadingElement>) => void;
};

type ChildTypes = {
  blockContextEntry: any;
  setSectionRef: (key: string, value: Nullable<HTMLHeadingElement>) => void;
  section: any;
  validationErrors: any;
  block: any;
  applicantIndex: any;
};

const getSelectFieldComponentOptions = (
  field: ApplicationFormSelectField,
): Partial<SelectProps> => {
  const groups: Record<string, OptionGroup<string | readonly string[]>> = {};
  const options: Array<Option<string | readonly string[]>> = [];
  each(field.options, (option) => {
    const o = {
      label: option.label,
      value: option.value,
      meta: option.meta,
    };
    if (option.group) {
      const group = groups[option.group] || {
        label: option.group,
        options: [],
      };

      group.options.push(o);
      groups[group.label] = group;
    } else {
      options.push(o);
    }
  });

  return {
    groups: valuesIn(groups),
    options,
    ...(field.nullable && { placeholder: 'Choose...' }),
  };
};

type FieldElementProps = {
  field: ApplicationFormInputField | ApplicationFormSelectField;
  fieldProps: any;
  contextAttr: any;
};

export const FieldElement: FC<FieldElementProps> = ({
  field: childField,
  fieldProps,
  contextAttr,
}) => {
  const { can } = useCan();

  let fieldElement;

  if (childField.fieldType === 'select') {
    const selectField: ApplicationFormSelectField =
      childField as ApplicationFormSelectField;
    fieldElement = (
      <FormField
        component={Select}
        {...getSelectFieldComponentOptions(selectField)}
        {...fieldProps}
      />
    );
  } else {
    const inputField: ApplicationFormInputField =
      childField as ApplicationFormInputField;
    let inputFieldProps = {};
    let nestedInputElement;

    if (inputField.type === 'percent') {
      inputFieldProps = {
        ...inputFieldProps,
        startElement: <CgMathPercent />,
        decimalPlaces: 7,
      };
      nestedInputElement = NumberInput;
    } else if (inputField.type === 'currency') {
      nestedInputElement = CurrencyInput;
      inputFieldProps = {
        ...inputFieldProps,
        defaultToZero: false,
      };
    } else if (inputField.type === 'date') {
      inputFieldProps = {
        ...inputFieldProps,
        disabled: !can(Permissions.FormUpdate),
      };
      nestedInputElement = DatePicker;
    } else if (inputField.type === 'longtext') {
      nestedInputElement = Textarea;
    } else if (inputField.type === 'date_month_full_year') {
      nestedInputElement = DatePattern;
      inputFieldProps = {
        ...inputFieldProps,
        datePattern: 'MM/YYYY',
      };
    } else if (inputField.type === 'wysiwyg') {
      nestedInputElement = Wysiwyg;
    } else {
      nestedInputElement = Input;
    }

    fieldElement = (
      <FormField
        component={nestedInputElement}
        {...fieldProps}
        {...inputFieldProps}
      />
    );
  }

  return childField.hideIfNull && contextAttr.value === null ? (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <></>
  ) : (
    <Box>{fieldElement}</Box>
  );
};

const getByContext = (array: ApplicationFormBlock[], context: string) =>
  array.find((item) => item.context.replace(/^.*~/, '') === context);

function getChildSectionTypeTitle(blockContextEntry: any) {
  const title: string[] = [];
  blockContextEntry?.children?.forEach((item: any) => {
    item?.attributes?.forEach((attribute: any) => {
      if (attribute.key === 'id' && attribute.identifier) {
        const parts = attribute.identifier.split('.');
        const name = parts.at(2);
        if (name) {
          title.push(
            name
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (c: string) => c.toUpperCase()),
          );
        }
      }
    });
  });
  return [...new Set(title)];
}

const ChildSection = ({
  blockContextEntry,
  setSectionRef,
  section,
  validationErrors,
  block,
  applicantIndex,
}: ChildTypes) => {
  const { can } = useCan();

  let previousExtractedTitle = '';
  let childTitleIndex = 0;
  const title = getChildSectionTypeTitle(blockContextEntry);

  function findObjectByContext(
    array: ApplicationFormBlock[],
    context: string,
  ): ApplicationFormBlock | undefined {
    return array.find((item) => item.context === `applicants~${context}`);
  }

  return (
    <Box mb={2}>
      {title.map((item, index) => {
        const sectionEl = findObjectByContext(
          section.children,
          snakeCase(item),
        );

        return (
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={index}>
            <Heading
              ref={(el) => {
                setSectionRef(`${sectionEl?.id}-${applicantIndex}`, el);
              }}
              css={{ scrollMarginTop: '15rem' }}
              my={4}
              size="md"
            >
              {item}
            </Heading>
            <Box borderColor="gray.300" borderLeft="4px solid" ml="6" pl="4">
              <Stack direction="column" gap={8}>
                {blockContextEntry.children?.map(
                  (childContextEntry: FormContextEntry, childIndex: number) => {
                    const blockChildContextAttributes = keyBy(
                      childContextEntry.attributes,
                      (a) => a.key,
                    );
                    const inputString =
                      childContextEntry.attributes[0].identifier;

                    const parts = inputString.split('.');
                    let extractedTitle = '';
                    let childrenDocumentType = '';
                    if (parts.length >= 3) {
                      const [_a, _b, titlePart] = parts;
                      extractedTitle = titlePart;
                      childrenDocumentType = titlePart;
                      extractedTitle = extractedTitle
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (c) => c.toUpperCase());
                    }
                    // Reset childIndex if extractedTitle changes
                    if (extractedTitle !== previousExtractedTitle) {
                      previousExtractedTitle = extractedTitle;
                      childTitleIndex = 0;
                    }
                    childTitleIndex += 1;

                    const childrenSection = getByContext(
                      section.children,
                      childrenDocumentType,
                    );

                    if (
                      childrenDocumentType.includes(
                        item.toLowerCase().replace(' ', '_'),
                      )
                    ) {
                      return (
                        <Box
                          // eslint-disable-next-line react/no-array-index-key
                          key={childIndex}
                          border="1px solid"
                          borderColor="gray.300"
                          borderRadius="8px"
                          p={4}
                        >
                          <Heading as="h3" mb={6} size="sm">
                            {block.entryTypeName.replace(/\b\w/g, (c: any) =>
                              c.toUpperCase(),
                            )}{' '}
                            #{applicantIndex + 1} - {extractedTitle} #
                            {childTitleIndex}
                          </Heading>
                          {childrenSection?.fields.map((childField) => {
                            const contextAttr =
                              blockChildContextAttributes[childField.key];
                            const fieldName = `${contextAttr.identifier}`;
                            const fieldProps = {
                              borderColor: 'gray.300',
                              required: !!childField.required,
                              readOnly: !!childField.readonly,
                              label: childField.label,
                              labelProps: {
                                optionalText: '',
                              },
                              disabled: !can(Permissions.FormUpdate),
                              manualError: validationErrors[fieldName]
                                ? validationErrors[fieldName][0]
                                : '',
                              name: fieldName,
                              variant: 'outline',
                            };

                            return (
                              <Box key={fieldName}>
                                <FieldElement
                                  contextAttr={contextAttr}
                                  field={childField}
                                  fieldProps={fieldProps}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    }
                    return null;
                  },
                )}
              </Stack>
            </Box>
          </Fragment>
        );
      })}
    </Box>
  );
};

const SideNav = ({
  hideLeftPanel,
  initialSectionRefs,
  formContexts,
  formSections,
  scrollTo,
}: SideNavTypes) => {
  const refsLoaded = useRef(false);
  const sectionRefs =
    useRef<Record<string, Nullable<HTMLHeadingElement>>>(initialSectionRefs);
  const [, setRerender] = useState(false);

  // periodically check if sectionRefs has loaded so we can check if ref exist and if yes - render it's List.Item component
  useEffect(() => {
    const interval = setInterval(() => {
      const refsExist = formSections.some((section) =>
        section.children.some(
          (child, index) => !!sectionRefs.current[`${child.id}-${index}`],
        ),
      );

      if (refsExist) {
        refsLoaded.current = true;
        setRerender(true); // Trigger a re-render
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [sectionRefs, formSections]);

  // Update sectionRefs with non-null elements since at first render we get null for all
  useEffect(() => {
    const updatedRefs: Record<string, HTMLHeadingElement> = {};

    Object.entries(initialSectionRefs).forEach(([key, element]) => {
      if (element !== null) {
        updatedRefs[key] = element;
      }
    });

    sectionRefs.current = updatedRefs;
  }, [initialSectionRefs]);

  return (
    <Box
      display={hideLeftPanel ? 'none' : 'block'}
      mr="12"
      position={hideLeftPanel ? 'static' : 'fixed'}
      w="255px"
    >
      <Box
        border="2px solid"
        borderColor="gray.200"
        borderRadius="2px"
        px={6}
        py={3}
        w="full"
      >
        <List.Root listStyleType="none" ml="0">
          {formSections.map((section: any, index: number) => {
            let totalContextEntries = 0;
            const block = section.parent.blocks[0];
            if (block) {
              const context: FormContext =
                formContexts[block.context || 'default'];
              totalContextEntries = context.entries.length;
            }
            return (
              <Fragment key={section.parent.id}>
                <List.Item
                  mt={3}
                  onClick={() =>
                    scrollTo(sectionRefs.current[section.parent.id])
                  }
                >
                  <Text cursor="pointer">
                    {section.parent.title !== '' && <>{index + 1}.</>}
                    {section.parent.title}
                    {totalContextEntries > 1 && (
                      <Text as="span"> ({totalContextEntries})</Text>
                    )}
                  </Text>
                </List.Item>

                {section.children.length >= 1 && (
                  <List.Item>
                    <List.Root ml={4}>
                      {formContexts?.applicants?.entries.map(
                        (entries, entryIndex) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Fragment key={entryIndex}>
                            <List.Item
                              cursor="pointer"
                              ml={4}
                              onClick={() =>
                                scrollTo(
                                  sectionRefs.current[
                                    section?.children?.[entryIndex]?.id
                                  ],
                                )
                              }
                            >
                              {`${section.parent.title.replace('s', '')} ${entryIndex + 1}`}
                            </List.Item>
                            <List.Root ml={8}>
                              {section.children.map(
                                (child: { id: number; context: string }) =>
                                  refsLoaded.current &&
                                  sectionRefs.current[
                                    `${child.id}-${entryIndex}`
                                  ] && (
                                    <List.Item
                                      // eslint-disable-next-line react/no-array-index-key
                                      key={`${child.id}-${entryIndex}`}
                                      onClick={() => {
                                        scrollTo(
                                          sectionRefs.current[
                                            `${child.id}-${entryIndex}`
                                          ],
                                        );
                                      }}
                                    >
                                      <Text cursor="pointer">
                                        {snakeCaseToSentenceCase(
                                          child.context.split('~').at(1) ??
                                            child.context,
                                        )}
                                      </Text>
                                    </List.Item>
                                  ),
                              )}
                            </List.Root>
                          </Fragment>
                        ),
                      )}
                    </List.Root>
                  </List.Item>
                )}
              </Fragment>
            );
          })}
        </List.Root>
      </Box>
    </Box>
  );
};

export const ApplicationUpdateForm: FC<ApplicationUpdateFormProps> = ({
  form,
  formContexts,
  validationErrors,
  onSubmit,
  isSaving = false,
  hideLeftPanel,
  showHiddenSave,
  onlyDirtyFields,
}) => {
  const sectionRefs = useRef<Record<string, Nullable<HTMLHeadingElement>>>({});
  const setSectionRef = useCallback(
    (key: string, value: Nullable<HTMLHeadingElement>) => {
      sectionRefs.current[key] = value;
    },
    [],
  );
  const { can } = useCan();

  const scrollTo = (ref: Nullable<HTMLHeadingElement>): void => {
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const formSections = form.sections
    .map((s) => ({
      parent: {
        id: s.id,
        title: s.title,
        blocks: s.blocks.filter((b) => !b.context.includes('~')),
      },
      children: s.blocks.filter((b) => b.context.includes('~')),
    }))
    .filter((s) => {
      const totalContextEntriesCount = sumBy(s.parent.blocks, (block) => {
        const context: FormContext | undefined = formContexts
          ? formContexts[block.context || 'default']
          : undefined;
        const totalContextEntries = context?.entries.length;
        return totalContextEntries || 0;
      });
      return totalContextEntriesCount > 0;
    });

  const formDefaultValues: any = {};

  formSections.forEach((section) => {
    section.parent.blocks.forEach((block) => {
      // @TODO: refactor this part as it's repeated several times
      const context: FormContext = formContexts[block.context || 'default'];
      let previousExtractedTitle = '';
      context.entries.forEach((blockContextEntry) => {
        const blockContextAttributes = keyBy(
          blockContextEntry.attributes,
          (a) => a.key,
        );
        block.fields.forEach((field) => {
          const contextAttr = blockContextAttributes[field.key];
          const fieldName = `${context.name}.${contextAttr.identifier}`;
          // formDefaultValues[fieldName] = contextAttr.value;
          set(formDefaultValues, fieldName, contextAttr.value);

          blockContextEntry.children?.forEach(
            (childContextEntry: FormContextEntry) => {
              const childBlockContextAttributes = keyBy(
                childContextEntry.attributes,
                (a) => a.key,
              );
              const inputString = childContextEntry.attributes[0].identifier;
              const parts = inputString.split('.');
              let extractedTitle = '';
              let childrenDocumentType = '';

              if (parts.length >= 3) {
                const [_a, _b, titlePart] = parts;
                extractedTitle = titlePart;
                childrenDocumentType = titlePart;
                extractedTitle = extractedTitle
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (c) => c.toUpperCase());
              }

              if (extractedTitle !== previousExtractedTitle) {
                previousExtractedTitle = extractedTitle;
              }

              const childrenSection = getByContext(
                section.children,
                childrenDocumentType,
              );

              childrenSection?.fields.forEach((childField) => {
                const childContextAttr =
                  childBlockContextAttributes[childField.key];
                const childFieldName = `${childContextAttr.identifier}`;
                set(formDefaultValues, childFieldName, childContextAttr.value);
              });
            },
          );
        });
      });
    });

    if (showHiddenSave) {
      set(formDefaultValues, 'formKey', form.key);
    }
  });

  const [searchParams] = useSearchParams();
  const htmlForm = useForm<any>({
    defaultValues: formDefaultValues,
  });
  const { handleSubmit, reset, formState } = htmlForm;

  const handleFormSubmit = (formValues: any) => {
    const filteredValues = Object.keys(formState.dirtyFields).reduce(
      (acc, contextKey) => {
        const contextDirtyFields = formState.dirtyFields[contextKey];

        if (typeof contextDirtyFields === 'object') {
          Object.keys(contextDirtyFields).forEach((uuid) => {
            const dirtyFieldsForUUID = contextDirtyFields[uuid];

            if (formValues[contextKey] && formValues[contextKey][uuid]) {
              Object.keys(dirtyFieldsForUUID).forEach((fieldKey) => {
                if (!acc[contextKey]) acc[contextKey] = {};
                if (!acc[contextKey][uuid]) acc[contextKey][uuid] = {};

                if (formValues[contextKey][uuid][fieldKey] !== undefined) {
                  acc[contextKey][uuid][fieldKey] =
                    formValues[contextKey][uuid][fieldKey];
                }
              });
            }
          });
        }
        return acc;
      },
      {} as Record<string, any>,
    );

    onSubmit(onlyDirtyFields ? filteredValues : formValues);
  };

  // Update form default values when formContexts changes
  useEffect(() => {
    const updatedFormDefaultValues = { ...formDefaultValues };

    formSections.forEach((section) => {
      section.parent.blocks.forEach((block) => {
        const context: FormContext = formContexts[block.context || 'default'];
        context.entries.forEach((blockContextEntry) => {
          const blockContextAttributes = keyBy(
            blockContextEntry.attributes,
            (a) => a.key,
          );
          block.fields.forEach((field) => {
            const contextAttr = blockContextAttributes[field.key];
            const fieldName = `${context.name}.${contextAttr.identifier}`;
            set(updatedFormDefaultValues, fieldName, contextAttr.value);
          });
        });
      });

      if (showHiddenSave) {
        set(updatedFormDefaultValues, 'formKey', form.key);
      }
    });
    reset(updatedFormDefaultValues);
  }, [formContexts]);

  // url on param change - check against refs on page scroll to matching includes
  useEffect(() => {
    const paramText = searchParams.get('link');

    if (paramText) {
      formSections.forEach(({ parent }) => {
        if (sectionRefs.current[parent.id]?.innerText.includes(paramText)) {
          scrollTo(sectionRefs.current[parent.id]);
        }
      });
    }
  }, [searchParams]);

  return (
    <FormProvider {...htmlForm}>
      <Flex
        as="form"
        direction="row"
        justifyContent="space-between"
        onSubmit={handleSubmit(handleFormSubmit)}
        w="full"
      >
        <SideNav
          formContexts={formContexts}
          formSections={formSections}
          hideLeftPanel={hideLeftPanel}
          initialSectionRefs={sectionRefs.current}
          scrollTo={scrollTo}
        />
        <Flex direction="column" flex="1" ml={hideLeftPanel ? '' : '285px'}>
          {formSections.map((section, index) => (
            <Box
              key={`section-${section.parent.id}`}
              borderBottom="1px solid"
              borderBottomColor="gray.300"
              mb={10}
              pb={10}
              w="full"
            >
              <Heading
                ref={(el) => {
                  sectionRefs.current[section.parent.id] = el;
                }}
                as="h3"
                css={{ scrollMarginTop: '15rem' }}
                mb={6}
                size="md"
              >
                {section.parent.title !== '' && <>{index + 1}.</>}
                {section.parent.title}
              </Heading>
              {section.parent.blocks.map((block) => {
                const context: FormContext =
                  formContexts[block.context || 'default'];
                const totalContextEntries = context.entries.length;
                return context.entries.map(
                  (
                    blockContextEntry: FormContextEntry,
                    blockEntryIndex: number,
                  ) => {
                    const blockContextAttributes = keyBy(
                      blockContextEntry.attributes,
                      (a) => a.key,
                    );
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <Box key={`block-${block.id}-${blockEntryIndex}`}>
                        {totalContextEntries >= 1 && (
                          <Text
                            ref={(el) => {
                              sectionRefs.current[
                                section?.children?.[blockEntryIndex]?.id
                              ] = el;
                            }}
                            css={{ scrollMarginTop: '15rem' }}
                            fontSize="lg"
                            fontWeight="semibold"
                            mb={6}
                            textTransform="capitalize"
                          >
                            {block.entryTypeName} #{blockEntryIndex + 1}
                          </Text>
                        )}
                        {block.fields.map((field) => {
                          const contextAttr = blockContextAttributes[field.key];
                          const fieldName = `${context.name}.${contextAttr.identifier}`;
                          const fieldProps = {
                            borderColor: 'gray.300',
                            // defaultValue: contextAttr.value,
                            requireed: !!field.required,
                            readOnly: !!field.readonly,
                            label: field.label,
                            labelProps: {
                              optionalText: '',
                            },
                            disabled: !can(Permissions.FormUpdate),
                            manualError: validationErrors[fieldName]
                              ? validationErrors[fieldName][0]
                              : '',
                            name: fieldName,
                            variant: 'outline',
                          };

                          return (
                            <Box key={fieldName}>
                              <FieldElement
                                contextAttr={contextAttr}
                                field={field}
                                fieldProps={fieldProps}
                              />
                            </Box>
                          );
                        })}

                        {blockContextEntry.children?.length !== 0 && (
                          <ChildSection
                            applicantIndex={blockEntryIndex}
                            block={block}
                            blockContextEntry={blockContextEntry}
                            section={section}
                            setSectionRef={setSectionRef}
                            validationErrors={validationErrors}
                          />
                        )}
                      </Box>
                    );
                  },
                );
              })}
            </Box>
          ))}
        </Flex>

        {can(Permissions.FormUpdate) && (
          <Box
            bottom={6}
            pointerEvents="none"
            position="fixed"
            textAlign="center"
            visibility={showHiddenSave ? 'hidden' : 'visible'}
            w="full"
            zIndex={1}
          >
            <Flex
              bgColor="gray.50"
              border="1px solid"
              borderColor="gray.200"
              display="inline-block"
              pointerEvents="all"
              px={8}
              py={10}
              textAlign="left"
            >
              <Button
                colorPalette="primary"
                loading={isSaving}
                loadingText="Saving..."
                onClick={handleSubmit(handleFormSubmit)}
                type="button"
                variant="solid"
              >
                Save changes
              </Button>
            </Flex>
          </Box>
        )}
        {showHiddenSave && (
          <button
            id={form.key}
            onClick={handleSubmit(handleFormSubmit)}
            style={{ visibility: 'hidden', position: 'absolute', width: '1px' }}
            type="submit"
          >
            Save
          </button>
        )}
      </Flex>
    </FormProvider>
  );
};
